import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";
import {
  AXIS_TICK,
  BAR_RADIUS,
  CHART_BASELINE,
  CHART_GRID,
  CHART_MARGIN,
  CHART_PRIMARY,
  MAX_BAR_SIZE,
  TICK_MARGIN,
  Y_AXIS_WIDTH,
  formatNumber,
} from "@/features/reports/components/chartTheme";
import { ChartEmpty } from "@/features/reports/components/ChartEmpty";
import { ChartTooltip } from "@/features/reports/components/ChartTooltip";

interface Props {
  /** Card title (e.g. "Distance, km" / "Time, h"). */
  title: string;
  /** Planned figure; `null`/`undefined` means missing for this metric. */
  planned: number | null;
  /** Actual figure; `null`/`undefined` means missing for this metric. */
  actual: number | null;
  /** Unit suffix for value labels / tooltip (e.g. "км" / "ч"). */
  unit?: string;
}

/**
 * Generic "Planned vs Actual" comparison chart (TASK-3829/3837). Reused for both
 * the distance (km) and time (h) panels — the parent converts/labels the raw
 * values, this component plots a planned bar next to an actual bar.
 *
 * Structure (TASK-3837): the two figures are modelled as TWO X-axis categories
 * ("Planned" / "Actual") rendered by a SINGLE `<Bar>` with a per-category
 * `<Cell>` color. This keeps the bars equal-width, centered, and adjacent — a
 * single data row with two `<Bar>` series splayed them to opposite plot edges
 * and did not read as a comparison.
 *
 * Null handling (TASK-3837): when BOTH figures are null/undefined the metric has
 * no data for the period, so we render the shared `<ChartEmpty />` instead of
 * plotting null as a 0-height bar. This keeps the chart consistent with the KPI
 * cards above, which show "—" for the same all-null case.
 *
 * Not-yet-recorded actual (TASK-3847): when `planned` has a real value but
 * `actual` is `null` (not measured yet), drawing a 0-height "Actual" bar next to
 * a tall "Planned" bar reads as a rendering glitch. In that case we drop the
 * Actual category entirely and surface an inline "actual not recorded yet" note
 * instead. A genuine measured `0` (not `null`) still plots as a real bar.
 *
 * Styling adopts the shared chart theme (TASK-3835/3836): brand palette, shared
 * tooltip, value labels, and a fixed Y-axis width so tick digits are never
 * clipped.
 */
export function PlanVsActualBarChart({ title, planned, actual, unit }: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const plannedLabel = t("analytics.pva.planned");
  const actualLabel = t("analytics.pva.actual");

  // No data for this metric in the selected period → mirror the KPI cards'
  // "—" empty state rather than drawing zero-height bars.
  const hasData = planned != null || actual != null;

  // Plan recorded but actual not yet measured (null, not a real 0). Suppress the
  // misleading 0-height Actual bar and show an inline note instead.
  const actualNotRecorded = planned != null && actual == null;

  const data = actualNotRecorded
    ? [{ name: plannedLabel, value: planned, fill: CHART_BASELINE }]
    : [
        { name: plannedLabel, value: planned ?? 0, fill: CHART_BASELINE },
        { name: actualLabel, value: actual ?? 0, fill: CHART_PRIMARY },
      ];

  const formatValue = (value: number | null | undefined): string =>
    formatNumber(value, { language, unit });

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {title}
      </p>
      {hasData ? (
        <>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_GRID}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={Y_AXIS_WIDTH}
                  tickMargin={TICK_MARGIN}
                  tickFormatter={(value: number) =>
                    formatNumber(value, { language })
                  }
                />
                <Tooltip
                  cursor={{ fill: CHART_GRID }}
                  content={
                    <ChartTooltip
                      valueFormatter={(value: ValueType) =>
                        formatValue(
                          typeof value === "number" ? value : Number(value),
                        )
                      }
                    />
                  }
                />
                <Bar
                  dataKey="value"
                  radius={BAR_RADIUS}
                  maxBarSize={MAX_BAR_SIZE}
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    formatter={(value: number) => formatValue(value)}
                    className="fill-gray-500 text-[10px]"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center justify-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-300"
                aria-hidden="true"
              />
              {plannedLabel}
            </span>
            {!actualNotRecorded && (
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm bg-[#0073EA]"
                  aria-hidden="true"
                />
                {actualLabel}
              </span>
            )}
          </div>
          {actualNotRecorded && (
            <p className="mt-1 text-center text-[11px] font-medium text-gray-400">
              {t("analytics.pva.actualNotRecorded")}
            </p>
          )}
        </>
      ) : (
        <ChartEmpty />
      )}
    </div>
  );
}
